import React, { useState, useEffect, useCallback, useRef } from 'react';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify } from '../../context/FeedbackContext';
import './ProductForm.css';
import './BundleProductForm.css';
import { FaRegTrashAlt } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';

const EMPTY_FORM = {
  tenantCode: '',
  itemName: '',
  itemDesc: '',
  catReference: '',
  basePrice: '0.00',
  memberPrice: '0.00',
  festivalPrice: '0.00',
  seasonal: 'regular',
  status: 'Published',
  printerName: '',
  printerReference: '',
};

const defaultBundleGroups = () => ([{
  id: Date.now(),
  name: 'Main course',
  limitedQty: 1,
  items: [
    { id: Date.now() + 1, subItemReference: '', qty: 1, priceAdj: '0' },
  ],
}]);

function BundleProductForm({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [simpleProducts, setSimpleProducts] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [bundleGroups, setBundleGroups] = useState(defaultBundleGroups());
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageInputRef = useRef(null);

  // 打印机列表
  const [prinList, setPrinList] = useState([]);

  const DESC_MAX = 50;

  const fetchCategories = useCallback(async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/catalog/categories/options?tenantReference=${selectedRestaurant.reference}`), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        setCategories(data.data);
        setForm(prev => prev.catReference ? prev : { ...prev, catReference: data.data[0]?.reference || '' });
      }
    } catch (err) { console.error('Failed to fetch categories', err); }
  }, [selectedRestaurant]);

  const fetchSimpleProducts = useCallback(async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/product/simple/all?tenantReference=${selectedRestaurant.reference}`), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200 && data.data) setSimpleProducts(data.data);
    } catch (err) { console.error('Failed to fetch simple products', err); }
  }, [selectedRestaurant]);

  useEffect(() => {
    fetchCategories();
    fetchSimpleProducts();
  }, [fetchCategories, fetchSimpleProducts]);

  useEffect(() => {
    if (selectedRestaurant) setForm(prev => ({ ...prev, tenantCode: selectedRestaurant.code }));
  }, [selectedRestaurant]);

  // 查询打印机
  const getPrintIn = async (tenantRef) => {
    if (!tenantRef) return;
    try {
      const res = await fetch(
        buildApiUrl(`/setting/printers/by-tenant?tenantReference=${tenantRef}`),
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.code === 200) setPrinList(data.data);
    } catch (err) {
      notify(err, 'error');
      console.error("Failed to fetch printers", err);
    }
  };

  useEffect(() => {
    getPrintIn(selectedRestaurant?.reference);
  }, [selectedRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  // 打印机选择
  const handleFormChangePrint = (e) => {
    const selectedReference = e.target.value;
    const selectedPrinter = prinList.find(p => p.reference === selectedReference);
    setForm(prev => ({
      ...prev,
      printerReference: selectedReference,
      printerName: selectedPrinter?.printerName || '',
    }));
    setFormErrors(prev => ({ ...prev, printerReference: undefined }));
  };

  // 选中分类后自动关联该分类的打印机
  useEffect(() => {
    if (!form.catReference || prinList.length === 0) {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
      return;
    }
    const selectedCat = categories.find(c => c.reference === form.catReference);
    if (selectedCat?.printerReference) {
      const matchedPrinter = prinList.find(p => p.reference === selectedCat.printerReference);
      if (matchedPrinter) {
        setForm(prev => ({
          ...prev,
          printerReference: matchedPrinter.reference,
          printerName: matchedPrinter.printerName,
        }));
      }
    } else {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
    }
  }, [form.catReference, categories, prinList]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const errors = {};
    if (!form.itemName.trim()) errors.itemName = 'Product name is required.';
    if (!form.itemDesc.trim()) errors.itemDesc = 'Product description is required.';
    else if (form.itemDesc.trim().length > DESC_MAX) errors.itemDesc = `Description cannot exceed ${DESC_MAX} characters.`;
    if (!form.printerReference) errors.printerReference = 'Please select a printer.';

    const limitQuantity = {};
    bundleGroups.forEach(g => {
      const limit = parseInt(g.limitedQty, 10);
      const subCount = g.items.length;
      if (!Number.isFinite(limit) || limit < 1) {
        limitQuantity[g.id] = 'Limited quantity cannot be less than 1.';
      } else if (limit > subCount) {
        limitQuantity[g.id] = `Limited quantity cannot exceed the number of products in this group (${subCount}).`;
      }
    });
    if (Object.keys(limitQuantity).length > 0) errors.limitQuantity = limitQuantity;

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setImageFile(file);
  };

  const addGroup = () => {
    setBundleGroups(prev => [...prev, {
      id: Date.now(),
      name: 'New group',
      limitedQty: 1,
      items: [{ id: Date.now() + 1, subItemReference: '', qty: 1, priceAdj: '0' }],
    }]);
  };

  const removeGroup = (groupId) => {
    setBundleGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const updateGroupName = (groupId, name) => {
    setBundleGroups(prev => prev.map(g => g.id === groupId ? { ...g, name } : g));
  };

  const updateGroupLimitedQty = (groupId, limitedQty) => {
    setBundleGroups(prev => prev.map(g => g.id === groupId ? { ...g, limitedQty } : g));
    setFormErrors(prev => {
      if (!prev.limitQuantity?.[groupId]) return prev;
      const nextLimit = { ...prev.limitQuantity };
      delete nextLimit[groupId];
      const next = { ...prev };
      if (Object.keys(nextLimit).length > 0) next.limitQuantity = nextLimit;
      else delete next.limitQuantity;
      return next;
    });
  };

  const addItem = (groupId) => {
    setBundleGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, items: [...g.items, { id: Date.now(), subItemReference: '', qty: 1, priceAdj: '0' }] }
        : g
    ));
  };

  const updateItem = (groupId, itemId, field, value) => {
    setBundleGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, items: g.items.map(it => it.id === itemId ? { ...it, [field]: value } : it) }
        : g
    ));
  };

  const removeItem = (groupId, itemId) => {
    setBundleGroups(prev => prev.map(g =>
      g.id === groupId ? { ...g, items: g.items.filter(it => it.id !== itemId) } : g
    ));
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        tenantReference: selectedRestaurant?.reference,
        tenantCode: form.tenantCode,
        catReference: form.catReference,
        itemName: form.itemName.trim(),
        itemDesc: form.itemDesc.trim(),
        basePrice: parseFloat(form.basePrice) || 0,
        memberPrice: parseFloat(form.memberPrice) || 0,
        festivalPrice: parseFloat(form.festivalPrice) || 0,
        seasonal: form.seasonal === 'seasonal' ? 1 : 0,
        status: form.status === 'Published' ? 1 : 0,
        printerReference: form.printerReference || null,
        groups: bundleGroups.map((g, gi) => ({
          groupName: g.name,
          limitQuantity: parseInt(g.limitedQty) || 1,
          position: gi + 1,
          subProducts: g.items.map((it, ii) => ({
            subItemReference: it.subItemReference,
            qty: parseInt(it.qty) || 1,
            priceAdjustment: parseFloat(it.priceAdj) || 0,
            position: ii + 1,
          })),
        })),
      };

      const formData = new FormData();
      formData.append('bundleProduct', JSON.stringify(payload));
      if (imageFile) formData.append('image', imageFile);

      const res = await fetch(buildApiUrl('/product/bundle/add'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      const data = await res.json();
      if (data.code === 200) {
        notify('Bundle product added successfully!', 'success');
        onNavigate('Product');
      } else {
        notify('Failed to add product: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Error adding bundle product:', err);
      notify('Failed to add product: ' + err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = form.itemName !== '';

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="pf-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item} className={item === 'Product' ? 'active' : ''}
              onClick={() => onNavigate(getRouteFromItem(item))}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="pf-main">
        <h1 className="pf-title">Add New Bundle Product</h1>

        <div className="pf-card">
          {/* Row: Product Name + Categories */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Product Name <span className="pf-required">*</span></label>
              <input
                className={`pf-input${formErrors.itemName ? ' pf-input-error' : ''}`}
                name="itemName"
                value={form.itemName}
                onChange={handleChange}
                placeholder="Enter product name"
              />
              {formErrors.itemName
                ? <span className="pf-field-error">{formErrors.itemName}</span>
                : <span className="pf-hint">A product name is required and recommended to be unique</span>
              }
            </div>
            <div className="pf-field">
              <label>Categories</label>
              <select className="pf-select" name="catReference" value={form.catReference} onChange={handleChange} disabled={categories.length === 0}>
                {categories.length === 0
                  ? <option value="" disabled style={{ color: '#aaa' }}>-- no categories added. --</option>
                  : categories.map(c => (
                      <option key={c.reference} value={c.reference}>{c.catName}</option>
                    ))}
              </select>
            </div>
          </div>

          {/* Product Description */}
          <div className="pf-field">
            <label>Product Description <span className="pf-required">*</span></label>
            <div className="pf-editor">
              <textarea
                className={`pf-textarea${formErrors.itemDesc ? ' pf-input-error' : ''}`}
                name="itemDesc"
                value={form.itemDesc}
                onChange={handleChange}
                placeholder="Provide a detailed description of your product"
                maxLength={DESC_MAX + 1}
              />
            </div>
            {formErrors.itemDesc && <span className="pf-field-error">{formErrors.itemDesc}</span>}
          </div>

          {/* Upload Image */}
          <div className="pf-field">
            <button type="button" className="pf-upload-btn" onClick={() => imageInputRef.current.click()}>
              Upload Product Images
            </button>
            {imageFile && <span className="pf-hint" style={{ marginTop: 8 }}>{imageFile.name}</span>}
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
          </div>

          {/* Row: Base Price + Seasonal */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Base Price</label>
              <input className="pf-input" name="basePrice" type="number" step="0.01" min="0" value={form.basePrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Seasonal</label>
              <select className="pf-select" name="seasonal" value={form.seasonal} onChange={handleChange}>
                <option value="regular">regular</option>
                <option value="seasonal">seasonal</option>
              </select>
            </div>
          </div>

          {/* Membership Price + Festival Price */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Membership Price</label>
              <input className="pf-input" name="memberPrice" type="number" step="0.01" min="0" value={form.memberPrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Festival Price</label>
              <input className="pf-input" name="festivalPrice" type="number" step="0.01" min="0" value={form.festivalPrice} onChange={handleChange} />
            </div>
          </div>

          {/* Bundle Configuration */}
          <div className="bpf-section">
            <div className="bpf-section-header">
              <span className="bpf-section-title">Bundle Configuration</span>
              <button type="button" className="bpf-add-group-btn" onClick={addGroup}>+ Add configuration group</button>
            </div>

            {bundleGroups.map((group, index) => (
              <div key={group.id} className="bpf-group">
                <div className="bpf-group-header">
                  <div className="bpf-group-name-wrap">
                    <input
                      className="bpf-group-name-input"
                      value={group.name}
                      onChange={e => updateGroupName(group.id, e.target.value)}
                    />
                    <FiEdit2 className="bpf-group-name-icon" />
                  </div>
                  <div className="bpf-group-header-actions">
                    <div className="bpf-group-qty-wrap">
                      <div className="bpf-group-qty">
                        <span className="bpf-qty-label">Limited quantity</span>
                        <input
                          className={`bpf-qty-input${formErrors.limitQuantity?.[group.id] ? ' pf-input-error' : ''}`}
                          type="number"
                          min="1"
                          value={group.limitedQty ?? 1}
                          onChange={e => updateGroupLimitedQty(group.id, e.target.value)}
                        />
                      </div>
                      {formErrors.limitQuantity?.[group.id] && (
                        <span className="pf-field-error">{formErrors.limitQuantity[group.id]}</span>
                      )}
                    </div>
                    {index > 0 && (
                      <button type="button" className="bpf-group-remove-btn" onClick={() => removeGroup(group.id)}>✕</button>
                    )}
                  </div>
                </div>

                <div className="bpf-table-header">
                  <span className="bpf-col-info">Product Info</span>
                  <span className="bpf-col-qty">Default Qty</span>
                  <span className="bpf-col-price">Price Adjustment</span>
                  <span className="bpf-col-action">Action</span>
                </div>

                {group.items.map(item => (
                  <div key={item.id} className="bpf-item-row">
                    <div className="bpf-col-info bpf-product-info">
                      <select
                        className="bpf-product-select"
                        value={item.subItemReference}
                        onChange={e => updateItem(group.id, item.id, 'subItemReference', e.target.value)}
                      >
                        <option value="" disabled>Select a product</option>
                        {simpleProducts.map(p => (
                          <option key={p.reference} value={p.reference}>{p.itemName}</option>
                        ))}
                      </select>
                    </div>
                    <div className="bpf-col-qty">
                      <input
                        className="bpf-qty-field"
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={e => updateItem(group.id, item.id, 'qty', e.target.value)}
                      />
                    </div>
                    <div className="bpf-col-price">
                      <div className="bpf-price-wrap">
                        <span>$</span>
                        <input
                          className="bpf-price-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.priceAdj}
                          onChange={e => updateItem(group.id, item.id, 'priceAdj', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="bpf-col-action">
                      <button type="button" className="bpf-delete-btn" onClick={() => removeItem(group.id, item.id)}>
                        <FaRegTrashAlt />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="bpf-group-footer">
                  <span className="bpf-items-count">{group.items.length} item{group.items.length !== 1 ? 's' : ''} selected</span>
                  <button type="button" className="bpf-add-item-btn" onClick={() => addItem(group.id)}>+ Add More Items</button>
                </div>
              </div>
            ))}
          </div>

          {/* Status + Printer */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Status</label>
              <select className="pf-select" name="status" value={form.status} onChange={handleChange}>
                <option>Published</option>
                <option>Unpublished</option>
              </select>
            </div>
            <div className="pf-field">
              <label>Printer <span className="pf-required">*</span></label>
              <select
                className={`pf-select${formErrors.printerReference ? ' pf-input-error' : ''}`}
                name="printerReference"
                value={form.printerReference || ''}
                onChange={handleFormChangePrint}
              >
                <option value="">-- Select Printer --</option>
                {prinList.map((printer) => (
                  <option key={printer.id} value={printer.reference}>
                    {printer.printerName}
                  </option>
                ))}
              </select>
              {formErrors.printerReference && <span className="pf-field-error">{formErrors.printerReference}</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pf-actions">
          <button className="pf-cancel-btn" onClick={() => onNavigate('Product')} disabled={isSubmitting}>Cancel</button>
          <button
            className={`pf-save-btn${hasChanges ? '' : ' pf-save-inactive'}`}
            onClick={handleSave}
            disabled={isSubmitting || !hasChanges}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  );
}

export default BundleProductForm;
